import { StyleRulesCallback, Theme, Typography, WithStyles, withStyles } from '@material-ui/core';
import * as React from 'react';
import { pure } from 'recompose';
import { required, Validator, minDate } from '../../../shared/validation';
import FormTextField from '../../../components/FormTextField';
import { Field } from 'redux-form';
import { BpM } from '../../../shared/brakepoints';
import FormSelectField from '../../../components/FormSelectField';
import FormDateField from '../../../components/FormDateField';
import Translator from '../../../components/Translator';
import { defaultMemoize } from 'reselect';

type CssClasses = 'fieldContainer' | 'fullRow' | 'bigRow';
const styles: StyleRulesCallback<CssClasses> = (theme: Theme) => ({
  fieldContainer: {
    display: 'flex',
    flexFlow: 'column',
    '& > *': {
      flex: '0 0 100%',
    },
    [BpM()]: {
      display: 'flex',
      margin: '0px -5px 0px',
      width: '500px',
      flexFlow: 'row wrap',
      '& > *': {
        flex: '0 0 250px',
        paddingLeft: '5px',
        paddingRight: '5px',
        maxWidth: '250px',
      },
    },
  },
  fullRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
  },
  bigRow: {
    flex: '0 0 100%',
    maxWidth: '100%',
    [BpM()]: {
      '& > *': {
        width: '320px',
        paddingRight: '10px',
      },
    },
  },
});

interface Props {}

class IdentityCardDisclosureFormData extends React.Component<Props & WithStyles<CssClasses>> {
  private translatedRequire = defaultMemoize((translation: string) => {
    return required(translation);
  });

  private justRequired = defaultMemoize((requiredValidator: Validator) => {
    return [requiredValidator];
  });

  private idCardExpiry = defaultMemoize((requiredMessage: string, expiryMessage: string) => {
    return [required(requiredMessage), minDate(expiryMessage)];
  });

  render() {
    let { classes } = this.props;
    return (
      <Translator>
        {translate => {
          const req = this.translatedRequire(translate('missing'));
          const justRequired = this.justRequired(req);
          const idCardExpiry = this.idCardExpiry(translate('missing'), translate('expiryError'));
          return (
            <>
              <Typography variant={'h6'}>{translate('identityCardDataTitle')}</Typography>
              <div className={classes.fieldContainer}>
                <div className={classes.bigRow}>
                  <Field
                    name="partner1IdentityCardType"
                    requireMark
                    component={FormSelectField}
                    validate={justRequired}
                    label={translate('identityCardType')}
                    fullWidth={true}
                    options={[
                      { value: '', text: translate('pleaseSelect') },
                      { value: '0', text: translate('cardTypeGermanIdentityCard') },
                      { value: '1', text: translate('cardTypeGermanPassport') },
                      { value: '2', text: translate('cardTypeEUIdentityCard') },
                      { value: '3', text: translate('cardTypeEUPassport') },
                      { value: '4', text: translate('cardTypeOtherPassport') },
                    ]}
                  />
                </div>
                <div className={classes.bigRow}>
                  <Field
                    name="partner1IdentityCardNumber"
                    requireMark
                    component={FormTextField}
                    validate={justRequired}
                    label={translate('identityCardNumber')}
                    fullWidth={true}
                  />
                </div>
                <Field
                  name="partner1IdentityCardIssuePlace"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('identityCardPlaceOfIssue')}
                  fullWidth={true}
                />
                <Field
                  name="partner1IdentityCardIssueAgency"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('identityCardIssueAgency')}
                  fullWidth={true}
                />
                <Field
                  name="partner1IdentityCardIssuedOn"
                  requireMark
                  component={FormDateField}
                  validate={justRequired}
                  label={translate('identityCardDateOfIssue')}
                  fullWidth={true}
                />
                <Field
                  name="partner1IdentityCardExpirationDate"
                  requireMark
                  component={FormDateField}
                  validate={idCardExpiry}
                  label={translate('identityCardDateOfExpire')}
                  fullWidth={true}
                  toolTipHtml={translate('expiryInfo')}
                />
                <div className={classes.bigRow}>
                  <Field
                    name="partner1DateOfBirth"
                    requireMark
                    component={FormDateField}
                    validate={justRequired}
                    label={translate('dateOfBirth')}
                    fullWidth={false}
                  />
                </div>
                <Field
                  name="partner1PlaceOfBirth"
                  requireMark
                  component={FormTextField}
                  validate={justRequired}
                  label={translate('placeOfBirth')}
                  fullWidth={true}
                />
                <Field
                  name="partner1CountryOfBirth"
                  requireMark
                  component={FormSelectField}
                  validate={justRequired}
                  label={translate('partner1CountryOfBirth')}
                  fullWidth={true}
                  options={[
                    { value: '', text: translate('pleaseSelect') },
                    { value: 'DE', text: translate('countryDE') },
                    { value: '', text: '-----------------' },
                    { value: 'AF', text: translate('countryAF') },
                    { value: 'EG', text: translate('countryEG') },
                    { value: 'AL', text: translate('countryAL') },
                    { value: 'DZ', text: translate('countryDZ') },
                    { value: 'VI', text: translate('countryVI') },
                    { value: 'AS', text: translate('countryAS') },
                    { value: 'AD', text: translate('countryAD') },
                    { value: 'AO', text: translate('countryAO') },
                    { value: 'AI', text: translate('countryAI') },
                    { value: 'AQ', text: translate('countryAQ') },
                    { value: 'AG', text: translate('countryAG') },
                    { value: 'GQ', text: translate('countryGQ') },
                    { value: 'AR', text: translate('countryAR') },
                    { value: 'AM', text: translate('countryAM') },
                    { value: 'AW', text: translate('countryAW') },
                    { value: 'AZ', text: translate('countryAZ') },
                    { value: 'ET', text: translate('countryET') },
                    { value: 'AU', text: translate('countryAU') },
                    { value: 'BS', text: translate('countryBS') },
                    { value: 'BH', text: translate('countryBH') },
                    { value: 'BD', text: translate('countryBD') },
                    { value: 'BB', text: translate('countryBB') },
                    { value: 'BY', text: translate('countryBY') },
                    { value: 'BE', text: translate('countryBE') },
                    { value: 'BZ', text: translate('countryBZ') },
                    { value: 'BJ', text: translate('countryBJ') },
                    { value: 'BM', text: translate('countryBM') },
                    { value: 'BT', text: translate('countryBT') },
                    { value: 'BO', text: translate('countryBO') },
                    { value: 'BQ', text: translate('countryBQ') },
                    { value: 'BA', text: translate('countryBA') },
                    { value: 'BW', text: translate('countryBW') },
                    { value: 'BV', text: translate('countryBV') },
                    { value: 'BR', text: translate('countryBR') },
                    { value: 'VG', text: translate('countryVG') },
                    { value: 'IO', text: translate('countryIO') },
                    { value: 'BN', text: translate('countryBN') },
                    { value: 'BG', text: translate('countryBG') },
                    { value: 'BF', text: translate('countryBF') },
                    { value: 'BI', text: translate('countryBI') },
                    { value: 'CL', text: translate('countryCL') },
                    { value: 'CN', text: translate('countryCN') },
                    { value: 'CK', text: translate('countryCK') },
                    { value: 'CR', text: translate('countryCR') },
                    { value: 'CI', text: translate('countryCI') },
                    { value: 'CW', text: translate('countryCW') },
                    { value: 'DK', text: translate('countryDK') },
                    { value: 'DM', text: translate('countryDM') },
                    { value: 'DO', text: translate('countryDO') },
                    { value: 'DJ', text: translate('countryDJ') },
                    { value: 'EC', text: translate('countryEC') },
                    { value: 'SV', text: translate('countrySV') },
                    { value: 'ER', text: translate('countryER') },
                    { value: 'EE', text: translate('countryEE') },
                    { value: 'FK', text: translate('countryFK') },
                    { value: 'FO', text: translate('countryFO') },
                    { value: 'FJ', text: translate('countryFJ') },
                    { value: 'FI', text: translate('countryFI') },
                    { value: 'FR', text: translate('countryFR') },
                    { value: 'TF', text: translate('countryTF') },
                    { value: 'PF', text: translate('countryPF') },
                    { value: 'GA', text: translate('countryGA') },
                    { value: 'GM', text: translate('countryGM') },
                    { value: 'GE', text: translate('countryGE') },
                    { value: 'GH', text: translate('countryGH') },
                    { value: 'GI', text: translate('countryGI') },
                    { value: 'GD', text: translate('countryGD') },
                    { value: 'GR', text: translate('countryGR') },
                    { value: 'GL', text: translate('countryGL') },
                    { value: 'U1', text: translate('countryU1') },
                    { value: 'GU', text: translate('countryGU') },
                    { value: 'GT', text: translate('countryGT') },
                    { value: 'GG', text: translate('countryGG') },
                    { value: 'GN', text: translate('countryGN') },
                    { value: 'GW', text: translate('countryGW') },
                    { value: 'GY', text: translate('countryGY') },
                    { value: 'HT', text: translate('countryHT') },
                    { value: 'HM', text: translate('countryHM') },
                    { value: 'HN', text: translate('countryHN') },
                    { value: 'HK', text: translate('countryHK') },
                    { value: 'IN', text: translate('countryIN') },
                    { value: 'ID', text: translate('countryID') },
                    { value: 'IM', text: translate('countryIM') },
                    { value: 'IQ', text: translate('countryIQ') },
                    { value: 'IR', text: translate('countryIR') },
                    { value: 'IE', text: translate('countryIE') },
                    { value: 'IS', text: translate('countryIS') },
                    { value: 'IL', text: translate('countryIL') },
                    { value: 'IT', text: translate('countryIT') },
                    { value: 'JM', text: translate('countryJM') },
                    { value: 'JP', text: translate('countryJP') },
                    { value: 'YE', text: translate('countryYE') },
                    { value: 'JE', text: translate('countryJE') },
                    { value: 'JO', text: translate('countryJO') },
                    { value: 'KY', text: translate('countryKY') },
                    { value: 'KH', text: translate('countryKH') },
                    { value: 'CM', text: translate('countryCM') },
                    { value: 'CA', text: translate('countryCA') },
                    { value: 'CV', text: translate('countryCV') },
                    { value: 'KZ', text: translate('countryKZ') },
                    { value: 'QA', text: translate('countryQA') },
                    { value: 'KE', text: translate('countryKE') },
                    { value: 'KG', text: translate('countryKG') },
                    { value: 'KI', text: translate('countryKI') },
                    { value: 'CC', text: translate('countryCC') },
                    { value: 'CO', text: translate('countryCO') },
                    { value: 'KM', text: translate('countryKM') },
                    { value: 'CD', text: translate('countryCD') },
                    { value: 'CG', text: translate('countryCG') },
                    { value: 'KP', text: translate('countryKP') },
                    { value: 'KR', text: translate('countryKR') },
                    { value: 'HR', text: translate('countryHR') },
                    { value: 'CU', text: translate('countryCU') },
                    { value: 'KW', text: translate('countryKW') },
                    { value: 'LA', text: translate('countryLA') },
                    { value: 'LS', text: translate('countryLS') },
                    { value: 'LV', text: translate('countryLV') },
                    { value: 'LB', text: translate('countryLB') },
                    { value: 'LR', text: translate('countryLR') },
                    { value: 'LY', text: translate('countryLY') },
                    { value: 'LI', text: translate('countryLI') },
                    { value: 'LT', text: translate('countryLT') },
                    { value: 'LU', text: translate('countryLU') },
                    { value: 'MO', text: translate('countryMO') },
                    { value: 'MG', text: translate('countryMG') },
                    { value: 'MW', text: translate('countryMW') },
                    { value: 'MY', text: translate('countryMY') },
                    { value: 'MV', text: translate('countryMV') },
                    { value: 'ML', text: translate('countryML') },
                    { value: 'MT', text: translate('countryMT') },
                    { value: 'MA', text: translate('countryMA') },
                    { value: 'MH', text: translate('countryMH') },
                    { value: 'MR', text: translate('countryMR') },
                    { value: 'MU', text: translate('countryMU') },
                    { value: 'YT', text: translate('countryYT') },
                    { value: 'MK', text: translate('countryMK') },
                    { value: 'MX', text: translate('countryMX') },
                    { value: 'FM', text: translate('countryFM') },
                    { value: 'MD', text: translate('countryMD') },
                    { value: 'MN', text: translate('countryMN') },
                    { value: 'ME', text: translate('countryME') },
                    { value: 'MS', text: translate('countryMS') },
                    { value: 'MZ', text: translate('countryMZ') },
                    { value: 'MM', text: translate('countryMM') },
                    { value: 'NA', text: translate('countryNA') },
                    { value: 'NR', text: translate('countryNR') },
                    { value: 'NP', text: translate('countryNP') },
                    { value: 'NC', text: translate('countryNC') },
                    { value: 'NZ', text: translate('countryNZ') },
                    { value: 'NI', text: translate('countryNI') },
                    { value: 'NL', text: translate('countryNL') },
                    { value: 'NE', text: translate('countryNE') },
                    { value: 'NG', text: translate('countryNG') },
                    { value: 'NU', text: translate('countryNU') },
                    { value: 'MP', text: translate('countryMP') },
                    { value: 'NF', text: translate('countryNF') },
                    { value: 'NO', text: translate('countryNO') },
                    { value: 'OM', text: translate('countryOM') },
                    { value: 'AT', text: translate('countryAT') },
                    { value: 'TL', text: translate('countryTL') },
                    { value: 'PK', text: translate('countryPK') },
                    { value: 'PW', text: translate('countryPW') },
                    { value: 'PA', text: translate('countryPA') },
                    { value: 'PG', text: translate('countryPG') },
                    { value: 'PY', text: translate('countryPY') },
                    { value: 'PE', text: translate('countryPE') },
                    { value: 'PH', text: translate('countryPH') },
                    { value: 'PN', text: translate('countryPN') },
                    { value: 'PL', text: translate('countryPL') },
                    { value: 'PT', text: translate('countryPT') },
                    { value: 'TW', text: translate('countryTW') },
                    { value: 'RW', text: translate('countryRW') },
                    { value: 'RO', text: translate('countryRO') },
                    { value: 'RU', text: translate('countryRU') },
                    { value: 'PM', text: translate('countryPM') },
                    { value: 'SB', text: translate('countrySB') },
                    { value: 'ZM', text: translate('countryZM') },
                    { value: 'WS', text: translate('countryWS') },
                    { value: 'SM', text: translate('countrySM') },
                    { value: 'ST', text: translate('countryST') },
                    { value: 'SA', text: translate('countrySA') },
                    { value: 'SE', text: translate('countrySE') },
                    { value: 'CH', text: translate('countryCH') },
                    { value: 'SN', text: translate('countrySN') },
                    { value: 'RS', text: translate('countryRS') },
                    { value: 'SC', text: translate('countrySC') },
                    { value: 'SL', text: translate('countrySL') },
                    { value: 'SG', text: translate('countrySG') },
                    { value: 'SX', text: translate('countrySX') },
                    { value: 'SK', text: translate('countrySK') },
                    { value: 'SI', text: translate('countrySI') },
                    { value: 'SO', text: translate('countrySO') },
                    { value: 'ES', text: translate('countryES') },
                    { value: 'LK', text: translate('countryLK') },
                    { value: 'SH', text: translate('countrySH') },
                    { value: 'KN', text: translate('countryKN') },
                    { value: 'LC', text: translate('countryLC') },
                    { value: 'VC', text: translate('countryVC') },
                    { value: 'PS', text: translate('countryPS') },
                    { value: 'ZA', text: translate('countryZA') },
                    { value: 'SD', text: translate('countrySD') },
                    { value: 'GS', text: translate('countryGS') },
                    { value: 'SS', text: translate('countrySS') },
                    { value: 'SR', text: translate('countrySR') },
                    { value: 'SZ', text: translate('countrySZ') },
                    { value: 'SY', text: translate('countrySY') },
                    { value: 'TJ', text: translate('countryTJ') },
                    { value: 'TZ', text: translate('countryTZ') },
                    { value: 'TH', text: translate('countryTH') },
                    { value: 'TG', text: translate('countryTG') },
                    { value: 'TK', text: translate('countryTK') },
                    { value: 'TO', text: translate('countryTO') },
                    { value: 'TT', text: translate('countryTT') },
                    { value: 'TD', text: translate('countryTD') },
                    { value: 'CZ', text: translate('countryCZ') },
                    { value: 'TN', text: translate('countryTN') },
                    { value: 'TR', text: translate('countryTR') },
                    { value: 'TM', text: translate('countryTM') },
                    { value: 'TC', text: translate('countryTC') },
                    { value: 'TV', text: translate('countryTV') },
                    { value: 'UG', text: translate('countryUG') },
                    { value: 'UA', text: translate('countryUA') },
                    { value: 'HU', text: translate('countryHU') },
                    { value: 'UM', text: translate('countryUM') },
                    { value: 'UY', text: translate('countryUY') },
                    { value: 'UZ', text: translate('countryUZ') },
                    { value: 'VU', text: translate('countryVU') },
                    { value: 'VA', text: translate('countryVA') },
                    { value: 'VE', text: translate('countryVE') },
                    { value: 'AE', text: translate('countryAE') },
                    { value: 'US', text: translate('countryUS') },
                    { value: 'VN', text: translate('countryVN') },
                    { value: 'WF', text: translate('countryWF') },
                    { value: 'CX', text: translate('countryCX') },
                    { value: 'CF', text: translate('countryCF') },
                    { value: 'CY', text: translate('countryCY') },
                  ]}
                />
              </div>
            </>
          );
        }}
      </Translator>
    );
  }
}

export default pure(withStyles(styles)(IdentityCardDisclosureFormData));



// WEBPACK FOOTER //
// ./src/routes/disclosure/components/IdentityCardDisclosureFormData.tsx